// ── PRIVACY JURISDICTION COPY PACKS (2e) ─────────────────────────────────────
// Three packs — uk_gdpr | us | npl — selected by the console-owned `country` flag.
//
// WHY THE PACK IS DERIVED FROM `country` RATHER THAN BEING ITS OWN FLAG:
// the brief asked for a copy-pack flag with values uk_gdpr|us|npl. A separate flag can
// contradict `country` — an instance set to country=US with pack=uk_gdpr would serve UK
// GDPR rights language to US users, which is a compliance bug that no test would catch
// because both flags would look individually "valid". Deriving the pack means the two can
// never disagree. `country` is already a console-owned flag, so this stays console-driven.
//
// UK AND NPL PACKS REPRODUCE THE EXISTING STRINGS VERBATIM. That is deliberate: it makes
// "UK and NPL privacy copy unchanged" checkable by reading this file, instead of trusting
// that no edit leaked across.
//
// NOTE ON SCOPE: this pack is PRESENTATION copy. The consent TEXT itself (the legal
// artifact that gets versioned and recorded against a consent_records row) lives
// server-side in consent_text_versions and is owned by backend/consent_policy.py.

const UK_GDPR = {
  key: 'uk_gdpr',
  subtitle: 'Manage your consent, data rights, and privacy preferences under UK GDPR.',
  // 2c — UK previously had NO commitments block, so the wellness-plane ring-fence was
  // stated on the US page only. The ring-fence is a factual statement about how the
  // product handles platform health-store data, and it is true on every instance; the
  // jurisdiction it is asserted in does not change it. UK now carries it too.
  commitments: [
    'Data from Apple Health or Google Health Connect is used for your wellness insights only — never for insurance, employment, or credit decisions.',
  ],
  rightsHeading: 'Your Data Rights',
  rightsIntro: 'Under UK GDPR, you have the right to access, correct, delete, and object to processing of your personal data.',
  downloadHeading: 'Download My Data',
  downloadBody: 'Export all your health data as a JSON file (Article 15 — Right of Access).',
  deleteHeading: 'Delete everything',
  deleteBodyPre: 'Permanently erase all your data (Article 17 — Right to Erasure): blood results, medicines, wearable data, documents, chat history ',
  deleteBodyStrong: 'and your Kalpa health memory',
  deleteBodyPost: '. This is immediate and cannot be undone.',
  automatedHeading: 'Automated Processing',
  automatedBody: 'Your biological age score is calculated automatically (Article 22). You can object to this processing.',
  geneticFooter: null,               // no extra line on UK
  stateField: false,
  footerLines: [
    // 2c — "Data Controller" is the correct GDPR label and stays. The ENTITY and CONTACT
    // were wrong: Kalpa Health Analytics Ltd is not the operating company, and dpo@ is not
    // a monitored address. Kept in sync with backend/gdpr.py's export `data_controller`.
    'Data Controller: Whitefriars Management Ltd, trading as Kalpa Health · Contact: privacy@kalpa.health',
    'For data correction requests (Article 16), please contact us directly.',
  ],
  medicalDisclaimer: null,           // UK carries its wellness framing elsewhere
};

// NPL is the same pack as UK today (the NPL instance runs the UK GDPR/IDTA posture,
// which is what the international-transfer consent is built around). Kept as its OWN
// entry rather than an alias so a future Nepal-specific notice has a place to land
// without touching the UK strings.
const NPL = { ...UK_GDPR, key: 'npl' };

const US = {
  key: 'us',
  subtitle: 'Manage your consent, data rights, and privacy preferences under applicable US federal and state privacy law.',
  commitments: [
    'We never share your health information with third parties for marketing purposes.',
    'We never sell your health data.',
    'Data from Apple Health or Google Health Connect is used for your wellness insights only — never for insurance, employment, or credit decisions.',
    "Where your state's privacy law is stricter than this notice, we follow your state's law.",
  ],
  rightsHeading: 'Your Data Rights',
  rightsIntro: 'You have the right to access, correct, and delete your personal data, and to object to automated processing of your health information.',
  downloadHeading: 'Download My Data',
  downloadBody: 'Export all your health data as a JSON file.',
  deleteHeading: 'Delete everything',
  // Same substance as UK, minus the Article 17 citation (no UK GDPR article applies here).
  deleteBodyPre: 'Permanently erase all your data: blood results, medicines, wearable data, documents, chat history ',
  deleteBodyStrong: 'and your Kalpa health memory',
  deleteBodyPost: '. This is immediate and cannot be undone.',
  automatedHeading: 'Automated Processing',
  automatedBody: 'Your biological age score is calculated automatically. You can object and request human review.',
  geneticFooter: 'Your genetic data is never used for insurance underwriting.',
  stateField: true,
  stateNote: 'State-specific provisions apply based on your state of residence.',
  footerLines: [
    'Operated by Whitefriars Management Ltd, trading as Kalpa Health · Privacy contact: privacy@kalpa.health',
  ],
  medicalDisclaimer: 'Kalpa is a wellness tool, not a medical device. It does not diagnose, treat, cure, or prevent any disease or condition.',
};

const PACKS = { UK: UK_GDPR, US, NP: NPL };

/** Resolve the pack for a country flag value. Unknown/absent -> UK (the historical default). */
export function privacyPack(country) {
  return PACKS[String(country || 'UK').toUpperCase()] || UK_GDPR;
}

// US states, for the profile field. Value stored is the 2-letter code.
export const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
  ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
  ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'],
  ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'],
  ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'],
  ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'],
  ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
